import { Component, type ErrorInfo, type ReactNode } from "react";
import { Section } from "@astryxdesign/core/Section";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Button } from "@astryxdesign/core/Button";
import { AlertTriangle } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// A render-time throw anywhere in the tree below this — a bug in a page
// component, a library incompatibility, a null a query result didn't
// actually guarantee — previously took the whole app down to a blank white
// screen with nothing on it to explain why or offer a way back. Query
// errors are already handled per page (RetryBanner) and mutation errors
// per dialog, but nothing caught a genuine component-RENDER exception;
// this is that missing catch-all, not a replacement for either.
//
// A class component because getDerivedStateFromError/componentDidCatch have
// no hook equivalent — this is the one place in the app that structurally
// requires one.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The one deliberate console use in this app: this is the last thing
    // standing between a crash and total silence, and there is no
    // error-reporting service wired in here to hand it to instead.
    // eslint-disable-next-line no-console
    console.error("Unhandled error in component tree:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <Section padding={8}>
          <EmptyState
            icon={<AlertTriangle size={32} />}
            title="Something went wrong"
            description="This page hit an unexpected error. Reloading usually clears it — if it keeps happening, tell whoever manages this system what you were doing when it broke."
            actions={<Button label="Reload" variant="primary" onClick={() => window.location.reload()} />}
          />
        </Section>
      );
    }
    return this.props.children;
  }
}
